import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TextField } from "@mui/material";
import React, { FunctionComponent, useState } from "react";
import { isValidDate } from "../../functions/is-valid-date";
import DateFnsAdapterWrapper from "../../util/DateFnsAdapterWrapper";
import { Guid } from "../../util/Guid";

/**
 * Date picker component properties. Contains options for configuring a date picker.
 */
type DatePickerProps = { options: DatePickerOptions };

/**
 * Date picker options type, provides settings for configurating a date picker.
 */
export type DatePickerOptions = {
	selectedDate?: Date;
	format: string;
	onChange?: (date: Date | null) => void;
	clear?: () => void;
	minDate?: Date;
	maxDate?: Date;
	defaultCalendarDate?: Date;
	placeholder: string;
	input: HTMLInputElement;
};

/**
 * Date picker state interface.
 */
interface DatePickerState {
	open?: boolean;
	selectedDate: Date | null;
	isInputInvalid: boolean;
	guid: string;
}

/**
 * Gets open calendar icon element.
 */
const OpenCalendarIcon = function () {
	return <i className="far fa-calendar-alt" />;
};

/**
 * IPF date picker component, renders a customised MUI date picker.
 * @param props Date picker configuration properties.
 * @see DatePicker.
 */
export const IpfDatePicker: FunctionComponent<DatePickerProps> = function (props: DatePickerProps) {
	const [state, setState] = useState({
		selectedDate: props.options.selectedDate,
		isInputInvalid: false,
		guid: Guid.newGuid()
	} as DatePickerState);

	const refreshState = () => {
		setState({ ...state });
	};

	/**
	 * Open date picker dialog handler.
	 */
	const handleOpen = () => {
		state.open = true;
		refreshState();
	};

	/**
	 * Close date picker dialog handler.
	 */
	const handleClose = () => {
		state.open = false;
		refreshState();
	};

	/**
	 * Resets validation.
	 */
	const resetValidation = () => {
		state.isInputInvalid = false;
	}

	/**
	 * Date picker value change handler. Handles typed input and date selection changes. 
	 * @param date
	 */
	const handleChange = (date: Date | null) => {

		// update state
		if (date && !isValidDate(date)) {
			state.selectedDate = date;		
		}
		else {

			if (date?.getTime() !== state.selectedDate?.getTime()) {
				state.selectedDate = date;
			}

			// reset validation if valid
			resetValidation();
		}

		// fire any provided on change handler
		if (props.options.onChange) {
			props.options.onChange(state.selectedDate);
		}
		refreshState();
	};

	/**
	 * Text input blur handler, validates input and refreshes state.
	 */
	const handleBlur = () => {

		resetValidation();

		if (state.selectedDate && !isValidDate(state.selectedDate)) {
			state.selectedDate = null;
			state.isInputInvalid = true;
		}
		else if (state.selectedDate) {
			if (props.options.maxDate && state.selectedDate > props.options.maxDate) {
				state.isInputInvalid = true;
			}
			else if (props.options.minDate && state.selectedDate < props.options.minDate) {
				state.isInputInvalid = true;
			}
		}
		else if (props.options.input.hasAttribute("data-val-required")) {
			state.isInputInvalid = true;
		}

		if (state.isInputInvalid) {
			refreshState();
		}
	}

	props.options.clear = () => {
		state.selectedDate = null;
		refreshState();
	};

	// setup date format format
	const defaultFormat = "dd/MM/yyyy";
	const format = props.options.format?.replace(/m/g, "M") ?? defaultFormat;

	// output configured MUI date picker
	return (
		<LocalizationProvider dateAdapter={DateFnsAdapterWrapper}>
			<DatePicker
				renderInput={(params: any) => {
					if (state.isInputInvalid && params.inputProps) {
						params.inputProps.className = "input-validation-error";
					}
					return <TextField {...params} onBlur={handleBlur} />;
				}}
				components={{ OpenPickerIcon: OpenCalendarIcon }}
				inputFormat={format}
				value={state.selectedDate ?? null}
				open={state.open ?? false}
				onOpen={handleOpen}
				onClose={handleClose}
				onChange={handleChange}
				OpenPickerButtonProps={{
					'aria-label': 'Select date',
				}}
				PopperProps={{ className: "date-picker-popover" }}
				minDate={props.options.minDate}
				maxDate={props.options.maxDate}
				defaultCalendarMonth={state.selectedDate ?? props.options.defaultCalendarDate ?? new Date()}
				ignoreInvalidInputs={false}
			/>
		</LocalizationProvider>
    );
}